import {
  afterNextRender,
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';

import { Room, RoomsStore } from '../../../state/rooms.repository';
import { PermissionStore } from '../../../state/permissions.repository';
import { RoomPlanTile } from './room-plan-tile/room-plan-tile';
import { allowedDoorSides, doorSideFor } from './room-plan-geometry';

/**
 * Browsable "building plan" of rooms: a responsive, scrollable grid of
 * floor-plan tiles (one per room), with the grid gaps acting as corridors.
 * Just the floor drawing — framing (fieldsets, page padding) belongs to the
 * hosts: the room login (a fixed-size preview fed via `rooms`) and the
 * `room-plan` main-nav section (all rooms from the store). Clicking a tile
 * emits `roomSelected`.
 */
@Component({
  selector: 'andor-room-plan',
  standalone: true,
  imports: [TranslatePipe, RoomPlanTile],
  templateUrl: './room-plan.html',
  styleUrl: './room-plan.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomPlan {
  /**
   * Optional pre-picked room list; overrides the store-derived visible rooms
   * (used by the room login's fixed-size plan preview). `null` = show all
   * visible rooms from the store.
   */
  readonly rooms = input<Room[] | null>(null);

  /** Tile to highlight as the current pick; scrolled into view when hidden. */
  readonly selectedRoomId = input('');

  /** Emits when the user picks a room by clicking (or Enter on) its tile. */
  readonly roomSelected = output<Room>();

  private readonly _rooms = inject(RoomsStore);
  private readonly _permissions = inject(PermissionStore);

  private readonly _rooms$ = toSignal(this._rooms.rooms$, { initialValue: [] as Room[] });
  // Hidden rooms (e.g. instant meetings) are never offered in browse lists —
  // they are reachable only by their id (see RoomsStore docs).
  private readonly _storeVisibleRooms = computed(() => this._rooms$().filter((r) => !r.isHidden));
  // `??`, not `||`: an empty `rooms` override (e.g. before the store loads)
  // must not fall back to the full store list.
  protected readonly visibleRooms = computed(() => this.rooms() ?? this._storeVisibleRooms());

  // The room to bring into view: the explicit pick, once it is among the
  // rendered tiles. A computed so the scroll effect below only fires when the
  // target actually changes, not on every presence delta.
  private readonly _scrollTargetId = computed(() => {
    const selected = this.selectedRoomId();
    return selected && this.visibleRooms().some((room) => room.roomId === selected) ? selected : '';
  });

  protected readonly bypassesGate = toSignal(this._permissions.bypassesJoinGate$, {
    initialValue: false,
  });

  // Coarse clock so closed doors swing open when a schedule window starts —
  // same cadence as ScheduleDisplay.
  protected readonly now = signal(Date.now());

  private readonly _grid = viewChild.required<ElementRef<HTMLElement>>('grid');
  // Actual rendered column count. The grid's auto-fill tracks depend on the
  // container width, so the CSS is the only source of truth — measured via
  // ResizeObserver instead of mirroring the minmax math in TS. (auto-fill
  // keeps empty tracks in the computed style, so the track count is the
  // column count regardless of how many rooms there are.)
  private readonly _columns = signal(2);

  // Door sides depend on grid adjacency (corridors exist only between rooms),
  // so they are resolved here where the room's position is known.
  protected readonly tiles = computed(() => {
    const rooms = this.visibleRooms();
    const columns = this._columns();
    return rooms.map((room, i) => ({
      room,
      doorSide: doorSideFor(room.roomId, allowedDoorSides(i, rooms.length, columns)),
    }));
  });

  // With a single row, doors may open downward (see `allowedDoorSides`) — the
  // grid then needs a bottom corridor so they don't open into the outer wall.
  protected readonly singleRow = computed(() => {
    const count = this.visibleRooms().length;
    return count > 0 && count <= this._columns();
  });

  constructor() {
    const destroyRef = inject(DestroyRef);

    const id = setInterval(() => this.now.set(Date.now()), 30_000);
    destroyRef.onDestroy(() => clearInterval(id));

    afterNextRender(() => {
      const grid = this._grid().nativeElement;
      const observer = new ResizeObserver(() => {
        this._columns.set(getComputedStyle(grid).gridTemplateColumns.split(' ').length);
      });
      observer.observe(grid);
      destroyRef.onDestroy(() => observer.disconnect());
    });

    // Bring the picked room into view once it left the visible area
    // ('nearest' keeps already-visible tiles untouched).
    afterRenderEffect(() => {
      const target = this._scrollTargetId();
      if (!target) return;
      this._grid()
        .nativeElement.querySelector(`[data-room-id="${CSS.escape(target)}"]`)
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }
}
